import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn, Crown, Play } from "lucide-react";
import { WEDDING_GALLERY, HOTEL } from "../data/site";

/**
 * Smart video tile:
 * - Always shows the poster image first (so the tile is never blank)
 * - Loads & autoplays the video only when scrolled into view
 * - Pauses the video when scrolled away to save bandwidth + battery
 */
function VideoTile({ src, poster, alt }) {
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            const p = el.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {poster && (
        <img
          src={poster}
          alt={alt}
          loading="lazy"
          aria-hidden="true"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            loaded ? "opacity-0" : "opacity-100"
          }`}
        />
      )}
      <video
        ref={videoRef}
        poster={poster}
        muted
        loop
        playsInline
        preload={inView ? "auto" : "none"}
        aria-label={alt}
        onLoadedData={() => setLoaded(true)}
        onCanPlay={() => setLoaded(true)}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
      >
        {inView ? <source src={src} type="video/mp4" /> : null}
      </video>
    </>
  );
}

export default function WeddingGallery() {
  const [active, setActive] = useState(null);
  const close = useCallback(() => setActive(null), []);
  const next = useCallback(
    () => setActive((i) => (i === null ? null : (i + 1) % WEDDING_GALLERY.length)),
    [],
  );
  const prev = useCallback(
    () =>
      setActive((i) => (i === null ? null : (i - 1 + WEDDING_GALLERY.length) % WEDDING_GALLERY.length)),
    [],
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, next, prev]);

  return (
    <section
      id="wedding-gallery"
      data-testid="wedding-gallery"
      className="section-padding bg-background relative"
    >
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 max-w-5xl">
          <div>
            <span className="eyebrow flex items-center gap-2">
              <Crown size={12} /> Wedding Gallery
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-foreground">
              Cinematic moments,<br />
              <span className="italic text-[#7A1A24] dark:text-[#D4AF37]">captured in candlelight.</span>
            </h2>
          </div>
          <a
            href={HOTEL.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wedding-gallery-book"
            className="btn-gold !py-3 !px-5 text-xs self-start"
          >
            Book Your Date
          </a>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[200px] sm:auto-rows-[240px] gap-3 sm:gap-4">
          {WEDDING_GALLERY.map((g, i) => {
            const isVideo = g.type === "video";
            return (
              <motion.button
                key={`${g.src}-${i}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ delay: i * 0.05, duration: 0.55 }}
                onClick={() => setActive(i)}
                data-testid={`wedding-gallery-image-${i}`}
                aria-label={`Open ${isVideo ? "wedding video" : "wedding image"}: ${g.alt}`}
                className={`group relative overflow-hidden rounded-2xl bg-muted ${g.span}`}
              >
                {isVideo ? (
                  <VideoTile src={g.src} poster={g.poster} alt={g.alt} />
                ) : (
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A0608]/80 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />
                <div className="absolute bottom-3 left-3 right-12 text-left text-white">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#E6C555] opacity-0 group-hover:opacity-100 transition-opacity">
                    {isVideo ? "Cinematic · Video" : "Wedding · Reception"}
                  </div>
                </div>
                {isVideo && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/95 text-[#1A1515] px-2.5 py-1 text-[9px] font-semibold tracking-[0.18em] uppercase">
                    <Play size={10} className="fill-[#1A1515]" /> Video
                  </div>
                )}
                <div className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-[#D4AF37]/95 grid place-items-center opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-90 transition-all">
                  {isVideo ? (
                    <Play size={16} className="text-[#1A1515] fill-[#1A1515]" />
                  ) : (
                    <ZoomIn size={16} className="text-[#1A1515]" />
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md grid place-items-center p-4"
            onClick={close}
            data-testid="wedding-gallery-lightbox"
          >
            <button
              onClick={close}
              aria-label="Close wedding gallery"
              data-testid="wedding-lightbox-close"
              className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"
            >
              <X size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Previous"
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Next"
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"
            >
              <ChevronRight size={20} />
            </button>

            {WEDDING_GALLERY[active].type === "video" ? (
              <motion.video
                key={active}
                src={WEDDING_GALLERY[active].src}
                controls
                autoPlay
                playsInline
                loop
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="max-h-[85vh] max-w-[92vw] rounded-2xl bg-black"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <motion.img
                key={active}
                src={WEDDING_GALLERY[active].src}
                alt={WEDDING_GALLERY[active].alt}
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="max-h-[85vh] max-w-[92vw] rounded-2xl object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-xs tracking-wide max-w-[80vw] text-center">
              {active + 1} / {WEDDING_GALLERY.length} — {WEDDING_GALLERY[active].alt}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
